import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Mail,
  MessageSquare,
  Webhook,
  CheckCircle,
  AlertCircle,
  Settings,
  Layout,
  Upload,
  Chrome,
  Facebook,
  RefreshCw,
  Link2,
  Shield,
} from 'lucide-react';
import { api } from '../lib/api.ts';
import { PageHeader } from '../components/ui/PageHeader.tsx';
import { Button } from '../components/ui/Button.tsx';
import { Modal, ModalFooter } from '../components/ui/Modal.tsx';
import { Input } from '../components/ui/Input.tsx';
import { Badge } from '../components/ui/Badge.tsx';

// ─── Types ────────────────────────────────────────────────────────────────────

interface IntegrationSettings {
  ses?: {
    fromAddress: string;
    fromName: string;
    configSetName: string;
    region: string;
    sesIdentity?: string;
  };
  sms?: {
    originationNumber: string;
    region: string;
  };
  webhook?: {
    defaultUrl: string;
    secret: string;
    timeoutMs: number;
  };
  oauth_google?: {
    clientId: string;
    clientSecret: string;
  };
  oauth_meta?: {
    appId: string;
    appSecret: string;
  };
  lygo?: {
    apiKey?: string;
    tenantId?: string;
  };
  auth_config?: {
    methods?: {
      emailPassword?: { enabled: boolean };
      google?: { enabled: boolean };
      meta?: { enabled: boolean };
      phoneOtp?: { enabled: boolean };
      magicLink?: { enabled: boolean };
    };
  };
}

interface PortalSettings {
  name?: string;
  logoUrl?: string;
  primaryColor?: string;
  secondaryColor?: string;
  fontFamily?: string;
  faviconUrl?: string;
}

interface SesIdentityStatus {
  identity: string | null;
  status: 'SUCCESS' | 'PENDING' | 'NOT_STARTED' | 'FAILED' | 'TEMPORARY_FAILURE' | 'NOT_CONFIGURED';
  dkimRecords: Array<{ name: string; type: string; value: string }>;
  configSetLinked: boolean;
  mailFromDomain: string | null;
  mailFromStatus: 'SUCCESS' | 'PENDING' | 'FAILED' | 'TEMPORARY_FAILURE' | null;
  mailFromRecords: Array<{ name: string; type: string; value: string }>;
}

interface SesAccountStatus {
  sandboxMode: boolean;
  max24HourSend: number;
  maxSendRate: number;
  sentLast24Hours: number;
}

interface Route53Zone {
  id: string;
  name: string;
}

// ─── Google Fonts list ────────────────────────────────────────────────────────

const FONT_OPTIONS = [
  'Inter',
  'Roboto',
  'Open Sans',
  'Lato',
  'Poppins',
  'Montserrat',
  'Nunito',
  'Raleway',
  'Source Sans 3',
  'Work Sans',
  'DM Sans',
  'Plus Jakarta Sans',
] as const;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function StatusIcon({ ok }: { ok: boolean }) {
  return ok ? (
    <CheckCircle size={14} className="text-emerald-500" />
  ) : (
    <AlertCircle size={14} className="text-zinc-300" />
  );
}

function IntegrationCard({
  title,
  description,
  icon,
  configured,
  onConfigure,
  children,
}: {
  title: string;
  description: string;
  icon: React.ReactNode;
  configured: boolean;
  onConfigure: () => void;
  children?: React.ReactNode;
}) {
  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-5 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-xl bg-zinc-100 flex items-center justify-center text-zinc-600 shrink-0">
            {icon}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-zinc-900">{title}</h3>
              <StatusIcon ok={configured} />
              {configured && <Badge variant="success">Configured</Badge>}
            </div>
            <p className="text-xs text-zinc-500 mt-0.5">{description}</p>
          </div>
        </div>
        <Button size="sm" variant="ghost" onClick={onConfigure}>
          <Settings size={12} className="mr-1.5" />
          Configure
        </Button>
      </div>
      {children && <div className="border-t border-zinc-50 pt-4">{children}</div>}
    </div>
  );
}

function KV({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-xs text-zinc-500">{label}</span>
      <span className="text-xs text-zinc-800 font-medium font-mono">{value}</span>
    </div>
  );
}

// ─── SES Modal ────────────────────────────────────────────────────────────────

function SesModal({
  open,
  onClose,
  initial,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  initial?: IntegrationSettings['ses'];
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    fromAddress: initial?.fromAddress ?? '',
    fromName: initial?.fromName ?? '',
    configSetName: initial?.configSetName ?? 'map-ses-config-set',
    region: initial?.region ?? 'eu-west-1',
    sesIdentity: initial?.sesIdentity ?? '',
  });

  // 'email' = verifica singolo indirizzo via link email
  // 'domain' = verifica dominio intero via DNS/DKIM
  const [identityMode, setIdentityMode] = useState<'email' | 'domain'>(
    initial?.sesIdentity ? 'domain' : 'email',
  );

  // Reset form + mode ogni volta che il modale si apre
  useEffect(() => {
    if (open) {
      setForm({
        fromAddress: initial?.fromAddress ?? '',
        fromName: initial?.fromName ?? '',
        configSetName: initial?.configSetName ?? 'map-ses-config-set',
        region: initial?.region ?? 'eu-west-1',
        sesIdentity: initial?.sesIdentity ?? '',
      });
      setIdentityMode(initial?.sesIdentity ? 'domain' : 'email');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // SES identity status
  const [identityStatus, setIdentityStatus] = useState<SesIdentityStatus | null>(null);
  const [identityLoading, setIdentityLoading] = useState(false);

  // Route53 zones (loaded once when modal opens in domain mode)
  const [r53Zones, setR53Zones] = useState<Route53Zone[]>([]);
  const [applyDnsLoading, setApplyDnsLoading] = useState(false);
  const [applyDnsResult, setApplyDnsResult] = useState<'ok' | 'error' | null>(null);

  // SES account status (sandbox + quota)
  const [accountStatus, setAccountStatus] = useState<SesAccountStatus | null>(null);

  // MAIL FROM domain state
  const [mailFromInput, setMailFromInput] = useState('');
  const [mailFromSaving, setMailFromSaving] = useState(false);
  const [mailFromResult, setMailFromResult] = useState<'ok' | 'error' | null>(null);
  const [applyMailFromDnsLoading, setApplyMailFromDnsLoading] = useState(false);
  const [applyMailFromDnsResult, setApplyMailFromDnsResult] = useState<'ok' | 'error' | null>(null);

  async function fetchIdentityStatus() {
    setIdentityLoading(true);
    try {
      const res = await api.get<SesIdentityStatus>('/api/v1/integrations/ses/identity-status');
      setIdentityStatus(res.data);
    } catch {
      setIdentityStatus(null);
    } finally {
      setIdentityLoading(false);
    }
  }

  async function fetchR53Zones() {
    try {
      const res = await api.get<{ zones: Route53Zone[] }>('/api/v1/integrations/ses/route53-zones');
      setR53Zones(res.data.zones);
    } catch {
      setR53Zones([]);
    }
  }

  // Dato un dominio, trova la zona Route53 più specifica che lo gestisce
  function findMatchingZone(domain: string): Route53Zone | null {
    let best: Route53Zone | null = null;
    for (const zone of r53Zones) {
      // Matcha se il dominio è uguale alla zona o è un sottodominio di essa
      if (domain === zone.name || domain.endsWith('.' + zone.name)) {
        if (!best || zone.name.length > best.name.length) {
          best = zone;
        }
      }
    }
    return best;
  }

  async function applyDnsRecords(hostedZoneId: string) {
    setApplyDnsLoading(true);
    setApplyDnsResult(null);
    try {
      await api.post('/api/v1/integrations/ses/apply-dns-records', { hostedZoneId });
      setApplyDnsResult('ok');
      // Dopo qualche secondo ricontrolla lo stato SES
      setTimeout(() => void fetchIdentityStatus(), 3000);
    } catch {
      setApplyDnsResult('error');
    } finally {
      setApplyDnsLoading(false);
    }
  }

  async function fetchAccountStatus() {
    try {
      const res = await api.get<SesAccountStatus>('/api/v1/integrations/ses/account-status');
      setAccountStatus(res.data);
    } catch {
      setAccountStatus(null);
    }
  }

  async function saveMailFromDomain() {
    setMailFromSaving(true);
    setMailFromResult(null);
    try {
      await api.put('/api/v1/integrations/ses/mail-from-domain', { mailFromDomain: mailFromInput });
      setMailFromResult('ok');
      // Ricarica identity status per aggiornare mailFromStatus/mailFromRecords
      setTimeout(() => void fetchIdentityStatus(), 1500);
    } catch {
      setMailFromResult('error');
    } finally {
      setMailFromSaving(false);
    }
  }

  async function applyMailFromDnsRecords(hostedZoneId: string) {
    if (!identityStatus?.mailFromRecords.length) return;
    setApplyMailFromDnsLoading(true);
    setApplyMailFromDnsResult(null);
    try {
      // Riutilizziamo apply-dns-records passando i record MAIL FROM via una route dedicata.
      // In alternativa, chiamiamo direttamente apply-dns-records con un flag mailFrom.
      // Per semplicità chiamo apply-dns-records che sa già la zona, ma qui passiamo
      // i record MAIL FROM tramite l'endpoint generico Route53.
      // Usiamo la stessa route ma con body esteso per i record MAIL FROM.
      // Per ora utilizziamo la route /ses/apply-mail-from-dns-records
      await api.post('/api/v1/integrations/ses/apply-mail-from-dns-records', { hostedZoneId });
      setApplyMailFromDnsResult('ok');
      setTimeout(() => void fetchIdentityStatus(), 3000);
    } catch {
      setApplyMailFromDnsResult('error');
    } finally {
      setApplyMailFromDnsLoading(false);
    }
  }

  // Fetch identity status + zone R53 + account status quando il modale si apre
  useEffect(() => {
    if (open && (initial?.fromAddress || initial?.sesIdentity)) {
      void fetchIdentityStatus();
      void fetchR53Zones();
      void fetchAccountStatus();
    } else if (!open) {
      setIdentityStatus(null);
      setApplyDnsResult(null);
      setAccountStatus(null);
      setMailFromInput('');
      setMailFromResult(null);
      setApplyMailFromDnsResult(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Quando arriva l'identity status, pre-popola il campo MAIL FROM con il valore attuale
  useEffect(() => {
    if (identityStatus?.mailFromDomain) {
      setMailFromInput(identityStatus.mailFromDomain);
    }
  }, [identityStatus?.mailFromDomain]);

  const [saved, setSaved] = useState(false);

  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: () =>
      api.patch('/api/v1/integrations/ses', {
        ...form,
        // in modalità email non salviamo sesIdentity (lo svuotiamo)
        sesIdentity: identityMode === 'domain' ? form.sesIdentity : '',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['integrations'] });
      onSaved();
      setSaved(true);
      void fetchIdentityStatus();
      void fetchR53Zones();
      void fetchAccountStatus();
    },
  });

  // Reset "saved" flag quando il modale si chiude
  useEffect(() => {
    if (!open) setSaved(false);
  }, [open]);

  const canSave = !!form.fromAddress && (identityMode === 'email' || !!form.sesIdentity);

  // ── SES identity banner ──
  function SesBanner() {
    if (identityLoading) {
      return (
        <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3 flex items-center gap-2 text-xs text-zinc-500">
          <RefreshCw size={12} className="animate-spin shrink-0" />
          Controllo stato identità SES…
        </div>
      );
    }

    if (!identityStatus || identityStatus.status === 'NOT_CONFIGURED') return null;

    const isVerified = identityStatus.status === 'SUCCESS';
    const isFailed =
      identityStatus.status === 'FAILED' || identityStatus.status === 'TEMPORARY_FAILURE';
    const isEmailIdentity = !!(identityStatus.identity && identityStatus.identity.includes('@'));
    const domain = isEmailIdentity
      ? identityStatus.identity!.split('@')[1]
      : identityStatus.identity;

    // Cerca zona Route53 compatibile (solo per domain identity)
    const matchedZone = !isEmailIdentity && domain ? findMatchingZone(domain) : null;

    return (
      <div
        className={`rounded-lg border p-3 space-y-2.5 text-xs ${
          isVerified
            ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
            : isFailed
              ? 'bg-red-50 border-red-200 text-red-800'
              : 'bg-amber-50 border-amber-200 text-amber-800'
        }`}
      >
        {/* Titolo + pulsante Ricontrolla */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-1.5 font-medium leading-snug">
            {isVerified ? (
              <>
                <CheckCircle size={13} className="mt-px shrink-0" />
                <span>
                  Identità verificata — le email inviate da{' '}
                  <span className="font-mono">{identityStatus.identity}</span> sono autorizzate da
                  Amazon SES. Bounce e complaint vengono tracciati automaticamente.
                </span>
              </>
            ) : isFailed ? (
              <>
                <AlertCircle size={13} className="mt-px shrink-0" />
                <span>
                  Verifica fallita per <span className="font-mono">{identityStatus.identity}</span>.{' '}
                  {isEmailIdentity
                    ? "Il link di verifica potrebbe essere scaduto. Salva di nuovo per rinviare l'email."
                    : 'Controlla che i record DNS siano stati aggiunti correttamente e attendi la propagazione.'}
                </span>
              </>
            ) : isEmailIdentity ? (
              <>
                <AlertCircle size={13} className="mt-px shrink-0" />
                <span>
                  Amazon SES ha inviato un'email di verifica a{' '}
                  <span className="font-mono">{identityStatus.identity}</span>. Apri quella casella
                  e clicca il link per completare la verifica. Poi premi{' '}
                  <span className="font-semibold">Ricontrolla</span>.
                </span>
              </>
            ) : (
              <>
                <AlertCircle size={13} className="mt-px shrink-0" />
                <span>
                  Verifica in corso per il dominio <span className="font-mono">{domain}</span>.
                  Aggiungi i 3 record CNAME qui sotto al DNS del dominio, poi premi{' '}
                  <span className="font-semibold">Ricontrolla</span>.
                </span>
              </>
            )}
          </div>
          <button
            type="button"
            onClick={() => void fetchIdentityStatus()}
            disabled={identityLoading}
            className="shrink-0 flex items-center gap-1 px-2 py-1 rounded-md border border-current opacity-70 hover:opacity-100 disabled:opacity-40 transition-opacity whitespace-nowrap"
          >
            <RefreshCw size={11} />
            Ricontrolla
          </button>
        </div>

        {/* Record DKIM — solo per domain identity non ancora verificata */}
        {!isEmailIdentity && identityStatus.dkimRecords.length > 0 && !isVerified && (
          <div className="space-y-1.5">
            <p className="font-semibold">Record CNAME da aggiungere al DNS:</p>
            {identityStatus.dkimRecords.map((r) => (
              <div
                key={r.name}
                className="bg-white/70 rounded-md p-2 font-mono text-[10px] break-all space-y-1"
              >
                <div className="flex items-start gap-1">
                  <span className="text-zinc-400 shrink-0 w-10">Nome</span>
                  <button
                    type="button"
                    className="text-left hover:underline cursor-copy"
                    onClick={() => void navigator.clipboard.writeText(r.name)}
                    title="Clicca per copiare"
                  >
                    {r.name}
                  </button>
                </div>
                <div className="flex items-start gap-1">
                  <span className="text-zinc-400 shrink-0 w-10">Tipo</span>
                  <span>{r.type}</span>
                </div>
                <div className="flex items-start gap-1">
                  <span className="text-zinc-400 shrink-0 w-10">Valore</span>
                  <button
                    type="button"
                    className="text-left hover:underline cursor-copy"
                    onClick={() => void navigator.clipboard.writeText(r.value)}
                    title="Clicca per copiare"
                  >
                    {r.value}
                  </button>
                </div>
              </div>
            ))}
            <p className="text-[10px] opacity-70">
              Clicca su nome o valore per copiarlo negli appunti.
            </p>

            {/* Pulsante Route53 auto — solo se dominio gestito nello stesso account AWS */}
            {matchedZone && (
              <div className="pt-1 border-t border-current/10 space-y-1.5">
                {applyDnsResult === 'ok' ? (
                  <p className="flex items-center gap-1.5 font-medium text-emerald-700">
                    <CheckCircle size={12} />
                    Record aggiunti in Route 53 (zona{' '}
                    <span className="font-mono">{matchedZone.name}</span>). La propagazione DNS
                    richiede qualche minuto — premi Ricontrolla tra poco.
                  </p>
                ) : applyDnsResult === 'error' ? (
                  <p className="flex items-center gap-1.5 font-medium text-red-700">
                    <AlertCircle size={12} />
                    Errore durante la scrittura in Route 53. Aggiungi i record manualmente.
                  </p>
                ) : (
                  <>
                    <p className="text-[10px] opacity-70">
                      Il dominio <span className="font-mono">{domain}</span> è gestito da Route 53
                      nello stesso account AWS (zona{' '}
                      <span className="font-mono">{matchedZone.name}</span>).
                    </p>
                    <button
                      type="button"
                      disabled={applyDnsLoading}
                      onClick={() => void applyDnsRecords(matchedZone.id)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white/80 border border-current font-medium hover:bg-white disabled:opacity-50 transition-colors"
                    >
                      {applyDnsLoading ? (
                        <>
                          <RefreshCw size={11} className="animate-spin" />
                          Creazione record in corso…
                        </>
                      ) : (
                        <>
                          <CheckCircle size={11} />
                          Aggiungi automaticamente in Route 53
                        </>
                      )}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="SES Email Configuration"
      description="Configure Amazon SES as your email sending provider"
    >
      <div className="space-y-4">
        {/* ── Segmented control tipo identità ── */}
        <div>
          <label className="block text-xs font-medium text-zinc-700 mb-1.5">
            Tipo identità SES
          </label>
          <div className="flex rounded-lg border border-zinc-200 overflow-hidden text-xs">
            <button
              type="button"
              onClick={() => {
                setIdentityMode('email');
                setForm((f) => ({ ...f, sesIdentity: '' }));
              }}
              className={`flex-1 px-3 py-2 font-medium transition-colors ${
                identityMode === 'email'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white text-zinc-600 hover:bg-zinc-50'
              }`}
            >
              Indirizzo email
            </button>
            <button
              type="button"
              onClick={() => setIdentityMode('domain')}
              className={`flex-1 px-3 py-2 font-medium border-l border-zinc-200 transition-colors ${
                identityMode === 'domain'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white text-zinc-600 hover:bg-zinc-50'
              }`}
            >
              Dominio intero
            </button>
          </div>
          <p className="text-xs text-zinc-400 mt-1">
            {identityMode === 'email'
              ? 'SES verifica il singolo indirizzo via email. Adatto per iniziare rapidamente.'
              : 'SES verifica il dominio via DNS (record CNAME). Permette di inviare da qualsiasi indirizzo del dominio.'}
          </p>
        </div>

        {/* ── Campo SES Identity Domain — solo in modalità dominio ── */}
        {identityMode === 'domain' && (
          <Input
            label="SES Identity Domain"
            value={form.sesIdentity}
            onChange={(e) => setForm((f) => ({ ...f, sesIdentity: e.target.value }))}
            placeholder="llt-map.justbit.it"
            hint="Il dominio che verrà registrato e verificato in Amazon SES tramite record DNS."
          />
        )}

        <Input
          label="From address"
          value={form.fromAddress}
          onChange={(e) => setForm((f) => ({ ...f, fromAddress: e.target.value }))}
          placeholder="noreply@llt-map.justbit.it"
          hint={
            identityMode === 'domain'
              ? 'Indirizzo mittente (deve appartenere al dominio verificato sopra)'
              : "Questo indirizzo sarà verificato come identità SES. SES invierà un'email di conferma."
          }
        />
        <Input
          label="From name"
          value={form.fromName}
          onChange={(e) => setForm((f) => ({ ...f, fromName: e.target.value }))}
          placeholder="Your Company"
        />
        <Input
          label="SES Configuration Set name"
          value={form.configSetName}
          onChange={(e) => setForm((f) => ({ ...f, configSetName: e.target.value }))}
          hint="Deployed by CDK as 'map-ses-config-set'"
        />
        <Input
          label="AWS Region"
          value={form.region}
          onChange={(e) => setForm((f) => ({ ...f, region: e.target.value }))}
          placeholder="eu-west-1"
        />
        {saved && (
          <p className="text-xs text-emerald-600 flex items-center gap-1">
            <CheckCircle size={12} />
            Configurazione salvata.
          </p>
        )}
        <SesBanner />

        {/* ── Account SES: sandbox + quota ── */}
        {accountStatus && (
          <div
            className={`rounded-lg border p-3 space-y-2 text-xs ${
              accountStatus.sandboxMode
                ? 'bg-amber-50 border-amber-200 text-amber-800'
                : 'bg-zinc-50 border-zinc-200 text-zinc-700'
            }`}
          >
            <div className="flex items-center justify-between gap-2">
              <p className="font-semibold flex items-center gap-1.5">
                {accountStatus.sandboxMode ? (
                  <>
                    <AlertCircle size={13} className="shrink-0" />
                    Account SES in modalità Sandbox
                  </>
                ) : (
                  <>
                    <CheckCircle size={13} className="shrink-0 text-emerald-600" />
                    Account SES in modalità Production
                  </>
                )}
              </p>
            </div>
            {accountStatus.sandboxMode && (
              <p className="text-[10px] leading-relaxed opacity-80">
                In sandbox puoi inviare email solo a indirizzi verificati manualmente nella console
                AWS. Per passare in produzione, richiedi l'accesso dalla console SES → Account
                dashboard → Request production access.
              </p>
            )}
            {/* Quota barra */}
            <div className="space-y-1">
              <div className="flex items-center justify-between text-[10px] opacity-70">
                <span>Invii nelle ultime 24h</span>
                <span className="font-mono">
                  {accountStatus.sentLast24Hours.toLocaleString()} /{' '}
                  {accountStatus.max24HourSend.toLocaleString()}
                </span>
              </div>
              <div className="w-full h-1.5 bg-black/10 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    accountStatus.sentLast24Hours / accountStatus.max24HourSend > 0.8
                      ? 'bg-red-500'
                      : accountStatus.sentLast24Hours / accountStatus.max24HourSend > 0.5
                        ? 'bg-amber-500'
                        : 'bg-emerald-500'
                  }`}
                  style={{
                    width: `${Math.min(
                      100,
                      (accountStatus.sentLast24Hours / (accountStatus.max24HourSend || 1)) * 100,
                    )}%`,
                  }}
                />
              </div>
              <p className="text-[10px] opacity-60">
                Rate massimo: {accountStatus.maxSendRate} email/sec
              </p>
            </div>
          </div>
        )}

        {/* ── MAIL FROM domain — solo per domain identity ── */}
        {identityMode === 'domain' &&
          identityStatus &&
          identityStatus.status !== 'NOT_CONFIGURED' && (
            <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3 space-y-2.5 text-xs text-zinc-700">
              <p className="font-semibold text-zinc-800">MAIL FROM domain (opzionale)</p>
              <p className="text-[10px] text-zinc-500 leading-relaxed">
                Personalizza il <span className="font-mono">Return-Path</span> delle email con un
                sottodominio tuo (es. <span className="font-mono">bounces.llt-map.justbit.it</span>
                ). Migliora l&apos;allineamento SPF/DMARC e la deliverability. Deve essere un
                sottodominio del dominio SES verificato.
              </p>
              {/* Stato corrente */}
              {identityStatus.mailFromDomain && (
                <div
                  className={`flex items-center gap-1.5 text-[10px] font-medium px-2 py-1 rounded-md ${
                    identityStatus.mailFromStatus === 'SUCCESS'
                      ? 'bg-emerald-100 text-emerald-700'
                      : identityStatus.mailFromStatus === 'FAILED'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-amber-100 text-amber-700'
                  }`}
                >
                  {identityStatus.mailFromStatus === 'SUCCESS' ? (
                    <CheckCircle size={11} />
                  ) : (
                    <AlertCircle size={11} />
                  )}
                  <span>
                    {identityStatus.mailFromDomain} —{' '}
                    {identityStatus.mailFromStatus === 'SUCCESS'
                      ? 'Verificato'
                      : identityStatus.mailFromStatus === 'FAILED'
                        ? 'Verifica fallita'
                        : 'In attesa di verifica DNS'}
                  </span>
                </div>
              )}
              {/* Input + pulsante salva */}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={mailFromInput}
                  onChange={(e) => {
                    setMailFromInput(e.target.value);
                    setMailFromResult(null);
                  }}
                  placeholder="bounces.llt-map.justbit.it"
                  className="flex-1 text-xs border border-zinc-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                />
                <button
                  type="button"
                  disabled={mailFromSaving || !mailFromInput}
                  onClick={() => void saveMailFromDomain()}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors whitespace-nowrap"
                >
                  {mailFromSaving ? <RefreshCw size={11} className="animate-spin" /> : null}
                  {mailFromInput && mailFromInput === identityStatus.mailFromDomain
                    ? 'Già configurato'
                    : 'Configura'}
                </button>
              </div>
              {mailFromResult === 'ok' && (
                <p className="text-[10px] text-emerald-600 flex items-center gap-1">
                  <CheckCircle size={11} />
                  MAIL FROM configurato. Aggiungi i record DNS qui sotto per completare la verifica.
                </p>
              )}
              {mailFromResult === 'error' && (
                <p className="text-[10px] text-red-500 flex items-center gap-1">
                  <AlertCircle size={11} />
                  Errore durante la configurazione. Il dominio deve essere un sottodominio
                  dell&apos;identity.
                </p>
              )}
              {/* Record DNS MAIL FROM */}
              {identityStatus.mailFromRecords.length > 0 &&
                identityStatus.mailFromStatus !== 'SUCCESS' &&
                (() => {
                  const mfDomain = identityStatus.mailFromDomain!;
                  const matchedZone = findMatchingZone(mfDomain);
                  return (
                    <div className="space-y-1.5">
                      <p className="font-semibold text-zinc-700">Record DNS da aggiungere:</p>
                      {identityStatus.mailFromRecords.map((r) => (
                        <div
                          key={r.name + r.type}
                          className="bg-white rounded-md border border-zinc-100 p-2 font-mono text-[10px] break-all space-y-1"
                        >
                          <div className="flex items-start gap-1">
                            <span className="text-zinc-400 shrink-0 w-10">Nome</span>
                            <button
                              type="button"
                              className="text-left hover:underline cursor-copy"
                              onClick={() => void navigator.clipboard.writeText(r.name)}
                              title="Clicca per copiare"
                            >
                              {r.name}
                            </button>
                          </div>
                          <div className="flex items-start gap-1">
                            <span className="text-zinc-400 shrink-0 w-10">Tipo</span>
                            <span>{r.type}</span>
                          </div>
                          <div className="flex items-start gap-1">
                            <span className="text-zinc-400 shrink-0 w-10">Valore</span>
                            <button
                              type="button"
                              className="text-left hover:underline cursor-copy"
                              onClick={() => void navigator.clipboard.writeText(r.value)}
                              title="Clicca per copiare"
                            >
                              {r.value}
                            </button>
                          </div>
                        </div>
                      ))}
                      {matchedZone && (
                        <div className="pt-1 border-t border-zinc-200 space-y-1.5">
                          {applyMailFromDnsResult === 'ok' ? (
                            <p className="flex items-center gap-1.5 text-emerald-700 font-medium">
                              <CheckCircle size={11} />
                              Record aggiunti in Route 53 (zona{' '}
                              <span className="font-mono">{matchedZone.name}</span>). Attendi la
                              propagazione DNS.
                            </p>
                          ) : applyMailFromDnsResult === 'error' ? (
                            <p className="flex items-center gap-1.5 text-red-600 font-medium">
                              <AlertCircle size={11} />
                              Errore durante la scrittura in Route 53. Aggiungi i record
                              manualmente.
                            </p>
                          ) : (
                            <button
                              type="button"
                              disabled={applyMailFromDnsLoading}
                              onClick={() => void applyMailFromDnsRecords(matchedZone.id)}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-zinc-100 border border-zinc-200 text-zinc-700 font-medium hover:bg-zinc-200 disabled:opacity-50 transition-colors"
                            >
                              {applyMailFromDnsLoading ? (
                                <>
                                  <RefreshCw size={11} className="animate-spin" />
                                  Creazione record…
                                </>
                              ) : (
                                <>
                                  <CheckCircle size={11} />
                                  Aggiungi automaticamente in Route 53
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}
            </div>
          )}

        {mutation.error && (
          <p className="text-xs text-red-500">
            {(mutation.error as { message?: string })?.message}
          </p>
        )}
        <ModalFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>
            {saved ? 'Chiudi' : 'Cancel'}
          </Button>
          <Button
            size="sm"
            loading={mutation.isPending}
            disabled={!canSave}
            onClick={() => mutation.mutate()}
          >
            Save
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
}

// ─── SMS Modal ────────────────────────────────────────────────────────────────

function SmsModal({
  open,
  onClose,
  initial,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  initial?: IntegrationSettings['sms'];
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    originationNumber: initial?.originationNumber ?? '',
    region: initial?.region ?? 'eu-west-1',
  });

  // Reset form to latest saved values each time the modal opens
  useEffect(() => {
    if (open) {
      setForm({
        originationNumber: initial?.originationNumber ?? '',
        region: initial?.region ?? 'eu-west-1',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const [saved, setSaved] = useState(false);

  // Reset "saved" flag when modal closes
  useEffect(() => {
    if (!open) setSaved(false);
  }, [open]);

  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => api.patch('/api/v1/integrations/sms', form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['integrations'] });
      onSaved();
      setSaved(true);
      // Non chiudiamo: l'utente vede la guida prima di chiudere
    },
  });

  // Determina region Pinpoint per costruire il link alla console AWS
  const pinpointRegion = form.region || 'eu-west-1';
  const pinpointConsoleUrl = `https://${pinpointRegion}.console.aws.amazon.com/pinpoint/home/?region=${pinpointRegion}#/sms-voice/phoneNumbers`;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="SMS Configuration"
      description="Configure AWS End User Messaging as your SMS provider"
    >
      <div className="space-y-4">
        <Input
          label="Origination number"
          value={form.originationNumber}
          onChange={(e) => setForm((f) => ({ ...f, originationNumber: e.target.value }))}
          placeholder="+39123456789"
          hint="Formato E.164. Deve essere un numero acquistato e attivo in AWS End User Messaging."
        />
        <Input
          label="AWS Region"
          value={form.region}
          onChange={(e) => setForm((f) => ({ ...f, region: e.target.value }))}
          placeholder="eu-west-1"
        />

        {/* Guida configurazione Pinpoint */}
        <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3 space-y-2 text-xs text-zinc-600">
          <p className="font-semibold text-zinc-700">Come ottenere un numero SMS su AWS</p>
          <ol className="list-decimal list-inside space-y-1 leading-relaxed">
            <li>
              Vai su{' '}
              <a
                href={pinpointConsoleUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-600 hover:underline font-medium"
              >
                AWS End User Messaging → Phone numbers
              </a>{' '}
              (regione: <span className="font-mono">{pinpointRegion}</span>)
            </li>
            <li>
              Clicca <span className="font-semibold">Request phone number</span> e scegli il paese e
              il tipo (Long code / Toll-free)
            </li>
            <li>Attendi l'approvazione (può richiedere ore o giorni per numeri italiani)</li>
            <li>
              Copia il numero in formato E.164 (es. <span className="font-mono">+39XXXXXXXXXX</span>
              ) e incollalo nel campo qui sopra
            </li>
            <li>
              Se sei in <span className="font-semibold">sandbox SMS</span>, puoi inviare SMS solo a
              numeri verificati manualmente nella console
            </li>
          </ol>
          {form.originationNumber && (
            <p className="text-zinc-500 pt-1">
              Numero configurato:{' '}
              <span className="font-mono font-medium text-zinc-700">{form.originationNumber}</span>
            </p>
          )}
        </div>

        {saved && (
          <p className="text-xs text-emerald-600 flex items-center gap-1">
            <CheckCircle size={12} />
            Configurazione salvata.
          </p>
        )}
        {mutation.error && (
          <p className="text-xs text-red-500">
            {(mutation.error as { message?: string })?.message}
          </p>
        )}
        <ModalFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>
            {saved ? 'Chiudi' : 'Cancel'}
          </Button>
          <Button
            size="sm"
            loading={mutation.isPending}
            disabled={!form.originationNumber}
            onClick={() => mutation.mutate()}
          >
            Save
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
}

// ─── Webhook Modal ────────────────────────────────────────────────────────────

function WebhookModal({
  open,
  onClose,
  initial,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  initial?: IntegrationSettings['webhook'];
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    defaultUrl: initial?.defaultUrl ?? '',
    secret: initial?.secret ?? '',
    timeoutMs: initial?.timeoutMs ?? 5000,
  });

  // Reset form to latest saved values each time the modal opens
  useEffect(() => {
    if (open) {
      setForm({
        defaultUrl: initial?.defaultUrl ?? '',
        secret: initial?.secret ?? '',
        timeoutMs: initial?.timeoutMs ?? 5000,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => api.patch('/api/v1/integrations/webhook', form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['integrations'] });
      onSaved();
      onClose();
    },
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Webhook Configuration"
      description="Configure outbound webhook dispatch for automation actions"
    >
      <div className="space-y-4">
        <Input
          label="Default webhook URL"
          value={form.defaultUrl}
          onChange={(e) => setForm((f) => ({ ...f, defaultUrl: e.target.value }))}
          placeholder="https://your-system.example.com/webhooks/map"
          hint="Used when a webhook action doesn't specify a custom URL"
        />
        <Input
          label="Signing secret"
          value={form.secret}
          onChange={(e) => setForm((f) => ({ ...f, secret: e.target.value }))}
          placeholder="whsec_..."
          hint="Used to sign requests with HMAC-SHA256. Leave blank to disable signing."
        />
        <Input
          label="Timeout (ms)"
          type="number"
          value={form.timeoutMs.toString()}
          onChange={(e) => setForm((f) => ({ ...f, timeoutMs: parseInt(e.target.value) || 5000 }))}
          hint="Max time to wait for webhook response before marking as failed"
        />
        {mutation.error && (
          <p className="text-xs text-red-500">
            {(mutation.error as { message?: string })?.message}
          </p>
        )}
        <ModalFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            loading={mutation.isPending}
            disabled={!form.defaultUrl}
            onClick={() => mutation.mutate()}
          >
            Save
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
}

// ─── Asset upload helper ──────────────────────────────────────────────────────

async function uploadAsset(file: File, type: 'logo' | 'favicon'): Promise<string> {
  const contentType = file.type || 'image/png';
  const res = await api.post<{ uploadUrl: string; assetUrl: string }>(
    `/api/v1/assets/upload?type=${type}&contentType=${encodeURIComponent(contentType)}`,
  );
  const { uploadUrl, assetUrl } = res.data;
  await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': contentType },
  });
  return assetUrl;
}

// ─── AssetField ───────────────────────────────────────────────────────────────

function AssetField({
  label,
  hint,
  value,
  onChange,
  assetType,
  accept,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (url: string) => void;
  assetType: 'logo' | 'favicon';
  accept: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    try {
      const url = await uploadAsset(file, assetType);
      onChange(url);
    } catch {
      setUploadError('Upload failed. Check bucket configuration.');
    } finally {
      setUploading(false);
      // reset so same file can be re-picked
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  return (
    <div className="space-y-1">
      <label className="block text-xs font-medium text-zinc-700">{label}</label>
      <div className="flex gap-2">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://cdn.example.com/..."
          className="flex-1 text-sm border border-zinc-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
        <button
          type="button"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium border border-zinc-200 rounded-lg text-zinc-600 hover:bg-zinc-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Upload size={12} />
          {uploading ? 'Uploading…' : 'Upload'}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="hidden"
          onChange={handleFile}
        />
      </div>
      {uploadError && <p className="text-xs text-red-500">{uploadError}</p>}
      {hint && <p className="text-xs text-zinc-400">{hint}</p>}
    </div>
  );
}

// ─── OAuth Google Modal ───────────────────────────────────────────────────────

function OAuthGoogleModal({
  open,
  onClose,
  initial,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  initial?: IntegrationSettings['oauth_google'];
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    clientId: initial?.clientId ?? '',
    clientSecret: initial?.clientSecret === '••••••' ? '' : (initial?.clientSecret ?? ''),
  });

  // Reset form to latest saved values each time the modal opens
  useEffect(() => {
    if (open) {
      setForm({
        clientId: initial?.clientId ?? '',
        clientSecret: initial?.clientSecret === '••••••' ? '' : (initial?.clientSecret ?? ''),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => api.patch('/api/v1/integrations/oauth_google', form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['integrations'] });
      onSaved();
      onClose();
    },
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Google SSO"
      description="Allow customers to sign in with their Google account"
    >
      <div className="space-y-4">
        <Input
          label="Client ID"
          value={form.clientId}
          onChange={(e) => setForm((f) => ({ ...f, clientId: e.target.value }))}
          placeholder="123456789-xxxx.apps.googleusercontent.com"
          hint="From Google Cloud Console → OAuth 2.0 Client IDs"
        />
        <Input
          label="Client secret"
          type="password"
          value={form.clientSecret}
          onChange={(e) => setForm((f) => ({ ...f, clientSecret: e.target.value }))}
          placeholder={initial?.clientSecret === '••••••' ? 'Leave blank to keep existing' : ''}
          hint="Leave blank to keep the existing secret when editing"
        />
        {mutation.error && (
          <p className="text-xs text-red-500">
            {(mutation.error as { message?: string })?.message}
          </p>
        )}
        <ModalFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            loading={mutation.isPending}
            disabled={!form.clientId}
            onClick={() => mutation.mutate()}
          >
            Save
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
}

// ─── OAuth Meta Modal ─────────────────────────────────────────────────────────

function OAuthMetaModal({
  open,
  onClose,
  initial,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  initial?: IntegrationSettings['oauth_meta'];
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    appId: initial?.appId ?? '',
    appSecret: initial?.appSecret === '••••••' ? '' : (initial?.appSecret ?? ''),
  });

  // Reset form to latest saved values each time the modal opens
  useEffect(() => {
    if (open) {
      setForm({
        appId: initial?.appId ?? '',
        appSecret: initial?.appSecret === '••••••' ? '' : (initial?.appSecret ?? ''),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => api.patch('/api/v1/integrations/oauth_meta', form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['integrations'] });
      onSaved();
      onClose();
    },
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Meta (Facebook) SSO"
      description="Allow customers to sign in with their Facebook account"
    >
      <div className="space-y-4">
        <Input
          label="App ID"
          value={form.appId}
          onChange={(e) => setForm((f) => ({ ...f, appId: e.target.value }))}
          placeholder="1234567890"
          hint="From Meta for Developers → Your App → App ID"
        />
        <Input
          label="App secret"
          type="password"
          value={form.appSecret}
          onChange={(e) => setForm((f) => ({ ...f, appSecret: e.target.value }))}
          placeholder={initial?.appSecret === '••••••' ? 'Leave blank to keep existing' : ''}
          hint="Leave blank to keep the existing secret when editing"
        />
        {mutation.error && (
          <p className="text-xs text-red-500">
            {(mutation.error as { message?: string })?.message}
          </p>
        )}
        <ModalFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            loading={mutation.isPending}
            disabled={!form.appId}
            onClick={() => mutation.mutate()}
          >
            Save
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
}

// ─── Lygo Modal ───────────────────────────────────────────────────────────────

function LygoModal({
  open,
  onClose,
  initial,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  initial?: IntegrationSettings['lygo'];
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    apiKey: initial?.apiKey === '••••••' ? '' : (initial?.apiKey ?? ''),
    tenantId: initial?.tenantId ?? '',
  });
  const [lygoTestResult, setLygoTestResult] = useState<{ ok: boolean; error?: string } | null>(
    null,
  );
  const [lygoTesting, setLygoTesting] = useState(false);

  // Reset form each time the modal opens
  useEffect(() => {
    if (open) {
      setForm({
        apiKey: initial?.apiKey === '••••••' ? '' : (initial?.apiKey ?? ''),
        tenantId: initial?.tenantId ?? '',
      });
      setLygoTestResult(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: () =>
      api.patch('/api/v1/integrations/lygo', {
        apiKey: form.apiKey,
        tenantId: form.tenantId,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['integrations'] });
      onSaved();
      onClose();
    },
  });

  async function handleTest() {
    setLygoTesting(true);
    setLygoTestResult(null);
    try {
      await api.get('/api/v1/integrations/lygo/test');
      setLygoTestResult({ ok: true });
    } catch (err) {
      const message =
        (err as { response?: { data?: { error?: string } }; message?: string })?.response?.data
          ?.error ??
        (err as { message?: string })?.message ??
        'Connessione fallita';
      setLygoTestResult({ ok: false, error: message });
    } finally {
      setLygoTesting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Lygo Shortlinks"
      description="Configure Lygo to generate tracked shortlinks in your campaigns"
    >
      <div className="space-y-4">
        <Input
          label="API Key"
          type="password"
          value={form.apiKey}
          onChange={(e) => {
            setForm((f) => ({ ...f, apiKey: e.target.value }));
            setLygoTestResult(null);
          }}
          placeholder={initial?.apiKey === '••••••' ? 'Lascia vuoto per mantenere la chiave esistente' : ''}
          hint="La chiave API del tuo account Lygo. Lascia invariata per mantenere quella esistente."
        />
        <Input
          label="Lygo Tenant ID"
          type="text"
          value={form.tenantId}
          onChange={(e) => {
            setForm((f) => ({ ...f, tenantId: e.target.value }));
            setLygoTestResult(null);
          }}
          placeholder="es. my-tenant"
          hint="L'identificativo del tenant nel sistema Lygo."
        />

        {/* Test connection */}
        <div className="flex items-center gap-3">
          <Button
            size="sm"
            variant="ghost"
            loading={lygoTesting}
            onClick={handleTest}
          >
            Testa connessione
          </Button>
          {lygoTestResult !== null && (
            <span
              className={`text-xs flex items-center gap-1 ${
                lygoTestResult.ok ? 'text-emerald-600' : 'text-red-500'
              }`}
            >
              {lygoTestResult.ok ? (
                <>
                  <CheckCircle size={12} />
                  Connessione riuscita
                </>
              ) : (
                <>
                  <AlertCircle size={12} />
                  {lygoTestResult.error}
                </>
              )}
            </span>
          )}
        </div>

        {mutation.error && (
          <p className="text-xs text-red-500">
            {(mutation.error as { message?: string })?.message}
          </p>
        )}
        <ModalFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Annulla
          </Button>
          <Button
            size="sm"
            loading={mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            Salva
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
}

// ─── Portal Branding Modal ────────────────────────────────────────────────────

function PortalModal({
  open,
  onClose,
  initial,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  initial?: PortalSettings;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    name: initial?.name ?? '',
    logoUrl: initial?.logoUrl ?? '',
    primaryColor: initial?.primaryColor ?? '#4F46E5',
    secondaryColor: initial?.secondaryColor ?? '#7C3AED',
    fontFamily: initial?.fontFamily ?? '',
    faviconUrl: initial?.faviconUrl ?? '',
  });

  // Reset form to latest saved values each time the modal opens
  useEffect(() => {
    if (open) {
      setForm({
        name: initial?.name ?? '',
        logoUrl: initial?.logoUrl ?? '',
        primaryColor: initial?.primaryColor ?? '#4F46E5',
        secondaryColor: initial?.secondaryColor ?? '#7C3AED',
        fontFamily: initial?.fontFamily ?? '',
        faviconUrl: initial?.faviconUrl ?? '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => api.patch('/api/v1/integrations/portal', form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portal-settings'] });
      onSaved();
      onClose();
    },
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Customer Portal Branding"
      description="Customise how the customer portal looks for your end users"
    >
      <div className="space-y-4">
        <Input
          label="Portal name"
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          placeholder="My Rewards"
          hint="Shown as the portal page title and in the login card header"
        />

        {/* Logo */}
        <AssetField
          label="Logo"
          hint="SVG or PNG recommended. Displayed in the portal header."
          value={form.logoUrl}
          onChange={(url) => setForm((f) => ({ ...f, logoUrl: url }))}
          assetType="logo"
          accept="image/png,image/jpeg,image/svg+xml,image/webp"
        />

        {/* Favicon */}
        <AssetField
          label="Favicon"
          hint=".ico or .png. Shown in the browser tab."
          value={form.faviconUrl}
          onChange={(url) => setForm((f) => ({ ...f, faviconUrl: url }))}
          assetType="favicon"
          accept="image/x-icon,image/png,image/svg+xml"
        />

        {/* Primary colour */}
        <div className="space-y-1">
          <label className="block text-xs font-medium text-zinc-700">Primary colour</label>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={form.primaryColor}
              onChange={(e) => setForm((f) => ({ ...f, primaryColor: e.target.value }))}
              className="w-10 h-10 rounded-lg border border-zinc-200 cursor-pointer p-0.5"
            />
            <input
              type="text"
              value={form.primaryColor}
              onChange={(e) => setForm((f) => ({ ...f, primaryColor: e.target.value }))}
              placeholder="#4F46E5"
              className="flex-1 text-sm border border-zinc-200 rounded-lg px-3 py-2 font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <p className="text-xs text-zinc-400">Used for primary buttons and key accents</p>
        </div>

        {/* Secondary colour */}
        <div className="space-y-1">
          <label className="block text-xs font-medium text-zinc-700">Secondary colour</label>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={form.secondaryColor}
              onChange={(e) => setForm((f) => ({ ...f, secondaryColor: e.target.value }))}
              className="w-10 h-10 rounded-lg border border-zinc-200 cursor-pointer p-0.5"
            />
            <input
              type="text"
              value={form.secondaryColor}
              onChange={(e) => setForm((f) => ({ ...f, secondaryColor: e.target.value }))}
              placeholder="#7C3AED"
              className="flex-1 text-sm border border-zinc-200 rounded-lg px-3 py-2 font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <p className="text-xs text-zinc-400">
            Used for outline buttons, links, badges and focus rings
          </p>
        </div>

        {/* Font family */}
        <div className="space-y-1">
          <label className="block text-xs font-medium text-zinc-700">Font family</label>
          <select
            value={form.fontFamily}
            onChange={(e) => setForm((f) => ({ ...f, fontFamily: e.target.value }))}
            className="w-full text-sm border border-zinc-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
          >
            <option value="">Default (Inter)</option>
            {FONT_OPTIONS.map((font) => (
              <option key={font} value={font}>
                {font}
              </option>
            ))}
          </select>
          <p className="text-xs text-zinc-400">
            Loaded from Google Fonts; applied across the entire portal
          </p>
        </div>

        {mutation.error && (
          <p className="text-xs text-red-500">
            {(mutation.error as { message?: string })?.message}
          </p>
        )}
        <ModalFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" loading={mutation.isPending} onClick={() => mutation.mutate()}>
            Save
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function IntegrationsPage() {
  const navigate = useNavigate();
  const [sesOpen, setSesOpen] = useState(false);
  const [smsOpen, setSmsOpen] = useState(false);
  const [webhookOpen, setWebhookOpen] = useState(false);
  const [portalOpen, setPortalOpen] = useState(false);
  const [googleOpen, setGoogleOpen] = useState(false);
  const [metaOpen, setMetaOpen] = useState(false);
  const [lygoOpen, setLygoOpen] = useState(false);

  const { data: settings } = useQuery<IntegrationSettings & { portal?: PortalSettings }>({
    queryKey: ['integrations'],
    queryFn: () => api.get('/api/v1/integrations').then((r) => r.data),
    retry: false,
  });

  const ses = settings?.ses;
  const sms = settings?.sms;
  const webhook = settings?.webhook;
  const portal = settings?.portal;
  const oauthGoogle = settings?.oauth_google;
  const oauthMeta = settings?.oauth_meta;
  const lygo = settings?.lygo;
  const authConfig = settings?.auth_config;

  // Count how many auth methods are enabled
  const authMethodCount = authConfig?.methods
    ? Object.values(authConfig.methods).filter((m) => m?.enabled).length
    : 1; // default: email+password

  return (
    <div>
      <PageHeader
        title="Integrations"
        description="Configure messaging providers, OAuth SSO, outbound webhooks, and portal branding"
      />

      <div className="space-y-4">
        {/* SES */}
        <IntegrationCard
          title="Amazon SES — Email"
          description="Send transactional and marketing emails via Amazon Simple Email Service"
          icon={<Mail size={18} />}
          configured={!!ses?.fromAddress}
          onConfigure={() => setSesOpen(true)}
        >
          {ses && (
            <div className="space-y-0">
              <KV label="From" value={`${ses.fromName} <${ses.fromAddress}>`} />
              <KV label="Config set" value={ses.configSetName} />
              <KV label="Region" value={ses.region} />
            </div>
          )}
        </IntegrationCard>

        {/* SMS */}
        <IntegrationCard
          title="AWS End User Messaging — SMS"
          description="Send SMS messages via AWS End User Messaging (formerly Pinpoint SMS)"
          icon={<MessageSquare size={18} />}
          configured={!!sms?.originationNumber}
          onConfigure={() => setSmsOpen(true)}
        >
          {sms && (
            <div className="space-y-0">
              <KV label="Origination number" value={sms.originationNumber} />
              <KV label="Region" value={sms.region} />
            </div>
          )}
        </IntegrationCard>

        {/* Google SSO */}
        <IntegrationCard
          title="Google SSO"
          description="Allow customers to sign in with their Google account via OAuth 2.0"
          icon={<Chrome size={18} />}
          configured={!!oauthGoogle?.clientId}
          onConfigure={() => setGoogleOpen(true)}
        >
          {oauthGoogle?.clientId && (
            <div className="space-y-0">
              <KV label="Client ID" value={oauthGoogle.clientId} />
              <KV label="Client secret" value="••••••" />
            </div>
          )}
        </IntegrationCard>

        {/* Meta SSO */}
        <IntegrationCard
          title="Meta (Facebook) SSO"
          description="Allow customers to sign in with their Facebook account via OAuth 2.0"
          icon={<Facebook size={18} />}
          configured={!!oauthMeta?.appId}
          onConfigure={() => setMetaOpen(true)}
        >
          {oauthMeta?.appId && (
            <div className="space-y-0">
              <KV label="App ID" value={oauthMeta.appId} />
              <KV label="App secret" value="••••••" />
            </div>
          )}
        </IntegrationCard>

        {/* Webhook */}
        <IntegrationCard
          title="Outbound Webhooks"
          description="POST to external systems when automation actions fire"
          icon={<Webhook size={18} />}
          configured={!!webhook?.defaultUrl}
          onConfigure={() => setWebhookOpen(true)}
        >
          {webhook && (
            <div className="space-y-0">
              <KV label="Default URL" value={webhook.defaultUrl} />
              <KV label="Timeout" value={`${webhook.timeoutMs}ms`} />
              <KV label="Signing" value={webhook.secret ? 'Enabled (HMAC-SHA256)' : 'Disabled'} />
            </div>
          )}
        </IntegrationCard>

        {/* Lygo Shortlinks */}
        <IntegrationCard
          title="Lygo Shortlinks"
          description="Generate and track shortlinks in campaigns via the Lygo platform"
          icon={<Link2 size={18} />}
          configured={!!lygo?.apiKey}
          onConfigure={() => setLygoOpen(true)}
        >
          {lygo?.apiKey && (
            <div className="space-y-0">
              <KV label="API Key" value="••••••" />
              {lygo.tenantId && <KV label="Tenant ID" value={lygo.tenantId} />}
            </div>
          )}
        </IntegrationCard>

        {/* Authentication Settings */}
        <IntegrationCard
          title="Authentication"
          description="Configure login methods, password policies, security limits, and token lifetimes"
          icon={<Shield size={18} />}
          configured={!!authConfig}
          onConfigure={() => navigate('/integrations/auth')}
        >
          <div className="space-y-0">
            <KV label="Active methods" value={`${authMethodCount} enabled`} />
          </div>
        </IntegrationCard>

        {/* Customer Portal Branding */}
        <IntegrationCard
          title="Customer Portal Branding"
          description="Customise the look and feel of your customer-facing loyalty portal"
          icon={<Layout size={18} />}
          configured={!!(portal?.name || portal?.logoUrl || portal?.primaryColor)}
          onConfigure={() => setPortalOpen(true)}
        >
          {portal && (portal.name || portal.logoUrl || portal.primaryColor) && (
            <div className="space-y-0">
              {portal.name && <KV label="Portal name" value={portal.name} />}
              {portal.logoUrl && <KV label="Logo URL" value={portal.logoUrl} />}
              {portal.faviconUrl && <KV label="Favicon URL" value={portal.faviconUrl} />}
              {portal.primaryColor && (
                <div className="flex items-center justify-between py-1">
                  <span className="text-xs text-zinc-500">Primary colour</span>
                  <div className="flex items-center gap-2">
                    <span
                      className="w-4 h-4 rounded-full border border-zinc-200 inline-block"
                      style={{ backgroundColor: portal.primaryColor }}
                    />
                    <span className="text-xs text-zinc-800 font-medium font-mono">
                      {portal.primaryColor}
                    </span>
                  </div>
                </div>
              )}
              {portal.secondaryColor && (
                <div className="flex items-center justify-between py-1">
                  <span className="text-xs text-zinc-500">Secondary colour</span>
                  <div className="flex items-center gap-2">
                    <span
                      className="w-4 h-4 rounded-full border border-zinc-200 inline-block"
                      style={{ backgroundColor: portal.secondaryColor }}
                    />
                    <span className="text-xs text-zinc-800 font-medium font-mono">
                      {portal.secondaryColor}
                    </span>
                  </div>
                </div>
              )}
              {portal.fontFamily && <KV label="Font" value={portal.fontFamily} />}
            </div>
          )}
        </IntegrationCard>
      </div>

      <SesModal open={sesOpen} onClose={() => setSesOpen(false)} initial={ses} onSaved={() => {}} />
      <SmsModal open={smsOpen} onClose={() => setSmsOpen(false)} initial={sms} onSaved={() => {}} />
      <OAuthGoogleModal
        open={googleOpen}
        onClose={() => setGoogleOpen(false)}
        initial={oauthGoogle}
        onSaved={() => {}}
      />
      <OAuthMetaModal
        open={metaOpen}
        onClose={() => setMetaOpen(false)}
        initial={oauthMeta}
        onSaved={() => {}}
      />
      <WebhookModal
        open={webhookOpen}
        onClose={() => setWebhookOpen(false)}
        initial={webhook}
        onSaved={() => {}}
      />
      <PortalModal
        open={portalOpen}
        onClose={() => setPortalOpen(false)}
        initial={portal}
        onSaved={() => {}}
      />
      <LygoModal
        open={lygoOpen}
        onClose={() => setLygoOpen(false)}
        initial={lygo}
        onSaved={() => {}}
      />
    </div>
  );
}
